'use client'

import { type RefObject, useEffect, useRef, useState } from 'react'

export type NumberFormat = 'plain' | 'currency' | 'percent'

export function formatValue(value: number, format: NumberFormat): string {
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(value)
    case 'percent':
      return `${Math.round(value)}%`
    default:
      return new Intl.NumberFormat('en-GB').format(Math.round(value))
  }
}

/** Whether an element has been scrolled into view yet. Latches once true. */
function useInView(ref: RefObject<Element | null>): boolean {
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node || inView) return

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.25 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [ref, inView])

  return inView
}

function easeOut(t: number): number {
  return 1 - (1 - t) ** 3
}

const DURATION_MS = 900

/**
 * A figure that counts up to its value when it scrolls into view.
 *
 * The server renders the finished value: with no JavaScript the screens read as complete
 * rather than sitting at zero. The reset to zero happens only while the element is off
 * screen, where nobody sees it, and the count starts inside the first animation frame, so
 * anywhere frames never run the number simply stays finished.
 */
export function AnimatedNumber({
  value,
  format = 'plain',
  className,
}: {
  readonly value: number
  readonly format?: NumberFormat
  readonly className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref)
  const [shown, setShown] = useState(value)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(value)
      return
    }
    if (!inView) {
      setShown(0)
      return
    }

    let frame = 0
    const start = performance.now()
    const step = (now: number) => {
      const progress = Math.min((now - start) / DURATION_MS, 1)
      setShown(value * easeOut(progress))
      if (progress < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [inView, value])

  return (
    <span className={className} ref={ref}>
      {formatValue(shown, format)}
    </span>
  )
}
